import React, { useEffect, useMemo, useState } from "react";
import { customersApi } from "../api";
import { useI18n } from "../i18n";
import SubscriptionBanner from "./SubscriptionBanner";
import { useOfflineCollection } from "../hooks/useOfflineCollection";
import { ENTITY_TYPES, SYNC_STATUS } from "../db";
import { dataService } from "../services/dataService";
import { syncService } from "../services/sync/syncService";
import { useOfflineOperations } from "../hooks/useOfflineOperations";
import { showToast, showConfirm } from "../utils/toast";

export default function Customers({ user, subStatus, onSubscribe }) {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState({ name: "", phone: "", address: "" });
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", phone: "", address: "" });
  const [q, setQ] = useState("");

  const {
    canPerformOperation: canManageCustomers,
    message: offlinePermissionMessage
  } = useOfflineOperations('customers');

  const isNetworkError = (error) => {
    if (!error) return false;
    const message = error.message || error.toString();
    return message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('request') && message.includes('failed');
  };

  const {
    data: offlineCustomers,
    loading: customersLoading,
    refresh: refreshCustomers
  } = useOfflineCollection({
    entityType: ENTITY_TYPES.CUSTOMER,
    fetcher: () => customersApi.list(),
    orgId: user?.orgId
  });

  useEffect(() => {
    setList(Array.isArray(offlineCustomers) ? offlineCustomers : []);
  }, [offlineCustomers]);
  useEffect(() => {
    try {
      const pre = sessionStorage.getItem('tp_prefill_customer_name');
      if (pre) {
        setDraft((s) => ({ ...s, name: pre }));
        sessionStorage.removeItem('tp_prefill_customer_name');
      }
    } catch {}
  }, []);

  const filtered = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) =>
      String(c.name || "").toLowerCase().includes(s) ||
      String(c.phone || "").toLowerCase().includes(s) ||
      String(c.address || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  const handleAdd = async () => {
    const name = (draft.name || "").trim();
    const phone = (draft.phone || "").trim();
    if (!name) return;
    
    if (!phone) {
      showToast({ message: 'Phone number is required', variant: 'warning' });
      return;
    }

    if (!canManageCustomers) {
      showToast({
        message: offlinePermissionMessage || 'Offline customer management is not available.',
        variant: 'warning',
      });
      return;
    }
    
    try {
      if (!navigator.onLine) {
        await syncService.queueCreate(ENTITY_TYPES.CUSTOMER, { name, phone, address: draft.address }, { orgId: user?.orgId });
        showToast({ message: 'Customer saved offline. It will sync automatically later.', variant: 'success' });
      } else {
        try {
          const created = await customersApi.create({ name, phone, address: draft.address });
          await dataService.upsertFromServer(ENTITY_TYPES.CUSTOMER, [created], { orgId: user?.orgId });
          await refreshCustomers({ force: true });
        } catch (error) {
          if (isNetworkError(error)) {
            await syncService.queueCreate(ENTITY_TYPES.CUSTOMER, { name, phone, address: draft.address }, { orgId: user?.orgId });
            showToast({ message: 'Customer saved offline. It will sync automatically later.', variant: 'success' });
          } else {
            throw error;
          }
        }
      }

      setDraft({ name: "", phone: "", address: "" });
      try { if (window && window.tpNavigate) window.tpNavigate('orders'); } catch {}
    } catch (e) {
      console.error("Failed to add customer", e);
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setEditDraft({ name: c.name || "", phone: c.phone || "", address: c.address || "" });
  };

  const saveEdit = async () => {
    if (!editing) return;
    
    if (!editDraft.phone.trim()) {
      showToast({ message: 'Phone number is required', variant: 'warning' });
      return;
    }

    if (!canManageCustomers) {
      showToast({
        message: offlinePermissionMessage || 'Offline customer management is not available.',
        variant: 'warning',
      });
      return;
    }
    
    try {
      const updates = { ...editDraft };
      if (editing?.syncStatus && editing.syncStatus !== SYNC_STATUS.SYNCED) {
        await dataService.upsertLocal(ENTITY_TYPES.CUSTOMER, { ...editing, ...updates }, { syncStatus: editing.syncStatus });
        setEditing(null);
        return;
      }

      if (!navigator.onLine) {
        await syncService.queueUpdate(ENTITY_TYPES.CUSTOMER, editing.customerId, updates);
        setEditing(null);
        showToast({ message: 'Customer update saved offline. It will sync later.', variant: 'success' });
        return;
      }

      try {
        const updated = await customersApi.update(editing.customerId, updates);
        await dataService.upsertFromServer(ENTITY_TYPES.CUSTOMER, [updated], { orgId: user?.orgId });
        await refreshCustomers({ force: true });
      } catch (error) {
        if (isNetworkError(error)) {
          await syncService.queueUpdate(ENTITY_TYPES.CUSTOMER, editing.customerId, updates);
          showToast({ message: 'Customer update saved offline. It will sync later.', variant: 'success' });
        } else {
          throw error;
        }
      }

      setEditing(null);
    } catch (e) {
      console.error("Failed to update customer", e);
    }
  };

  const remove = async (c) => {
    const confirmed = await showConfirm({
      title: t('cust.confirm.delete'),
      message: t('cust.confirm.delete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      destructive: true,
      variant: 'error',
    });
    if (!confirmed) return;

    if (!canManageCustomers) {
      showToast({
        message: offlinePermissionMessage || 'Offline customer management is not available.',
        variant: 'warning',
      });
      return;
    }
    try {
      if (!navigator.onLine) {
        await syncService.queueDelete(ENTITY_TYPES.CUSTOMER, c.customerId);
        await dataService.removeEntity(ENTITY_TYPES.CUSTOMER, c.customerId);
        showToast({ message: 'Customer delete queued. It will sync when online.', variant: 'success' });
      } else {
        try {
          await customersApi.remove(c.customerId);
          await dataService.removeEntity(ENTITY_TYPES.CUSTOMER, c.customerId);
          await refreshCustomers({ force: true });
        } catch (error) {
          if (isNetworkError(error)) {
            await syncService.queueDelete(ENTITY_TYPES.CUSTOMER, c.customerId);
            await dataService.removeEntity(ENTITY_TYPES.CUSTOMER, c.customerId);
            showToast({ message: 'Customer delete queued. It will sync when online.', variant: 'success' });
          } else {
            throw error;
          }
        }
      }
    } catch (e) {
      console.error("Failed to delete customer", e);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      <div className="text-lg font-bold mb-3">{t('cust.title')}</div>

      <SubscriptionBanner 
        user={user} 
        subStatus={subStatus} 
        onSubscribe={onSubscribe}
        showCreateButton={true}
        quotaInfo={subStatus?.quotaInfo}
      />

      {!canManageCustomers && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-2xl px-3 py-2 text-xs mb-3">
          {offlinePermissionMessage || 'Offline customer management is not available for your plan.'}
        </div>
      )}

      <div className="bg-green-50 rounded-2xl p-4 border border-green-100 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input className="border rounded px-3 py-2 text-sm" placeholder={t('cust.add.name')} value={draft.name} onChange={(e)=>setDraft({...draft, name:e.target.value})} />
          <input className="border rounded px-3 py-2 text-sm" placeholder={t('cust.add.phone')} value={draft.phone} onChange={(e)=>setDraft({...draft, phone:e.target.value})} required />
          <input className="border rounded px-3 py-2 text-sm sm:col-span-3" placeholder={t('cust.add.address')} value={draft.address} onChange={(e)=>setDraft({...draft, address:e.target.value})} />
        </div>
        <button 
          className="mt-3 w-full sm:w-auto bg-green-600 text-white rounded-xl px-4 py-2 text-sm shadow" 
          onClick={() => {
            // Check if subscription is inactive
            if (subStatus?.valid === false) {
              onSubscribe?.();
              return;
            }
            // Check if trial user has exceeded quota
            if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
              onSubscribe?.();
              return;
            }
            handleAdd();
          }}
        >
          {t('cust.add.save')}
        </button>
      </div>

      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm mb-3">
        <div className="text-sm font-semibold mb-2">{t('cust.list.title')}</div>
        <input className="border rounded px-3 py-2 text-sm w-full mb-3" placeholder={t('cust.search.placeholder')} value={q} onChange={(e)=>setQ(e.target.value)} />
        {customersLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500"><div className="h-5 w-5 rounded-full border-4 border-green-600 border-t-transparent animate-spin"></div><span>{t('cust.loading')}</span></div>
        ) : (
          <div className="divide-y">
            {filtered.map((c) => (
              <div
                key={c.customerId}
                className={`py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED ? 'bg-yellow-50/60 border border-yellow-200 rounded-xl px-3' : ''}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 truncate flex items-center gap-2">
                    <span>{c.name}</span>
                    {c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-yellow-800 bg-yellow-200/80 px-2 py-0.5 rounded-full">
                        ⏳ {t('cust.status.pendingSync') || 'Pending sync'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 truncate">{c.phone}{c.address ? ` · ${c.address}` : ''}</div>
                </div>
                <div className="flex items-center gap-3 sm:ml-2">
                  <button
                    className={`text-xs underline ${c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED ? 'text-gray-400 cursor-not-allowed' : 'text-green-600'}`}
                    onClick={()=>{ if (c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED) return; openEdit(c); }}
                    disabled={c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED}
                  >{t('cust.action.edit')}</button>
                  <button
                    className={`text-xs underline ${c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED ? 'text-gray-400 cursor-not-allowed' : 'text-red-600'}`}
                    onClick={()=>{ if (c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED) return; remove(c); }}
                    disabled={c.syncStatus && c.syncStatus !== SYNC_STATUS.SYNCED}
                  >{t('cust.action.delete')}</button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="text-xs text-gray-400">{t('cust.empty')}</div>}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-green-900/10">
          <div className="w-full sm:w-[420px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-green-100">
            <div className="p-4 border-b border-green-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">{t('cust.modal.editTitle')}</div>
              <button className="text-sm text-gray-500" onClick={()=>setEditing(null)}>✕</button>
            </div>
            <div className="p-4 grid gap-2">
              <input className="border rounded px-3 py-2 text-sm" placeholder={t('cust.add.name')} value={editDraft.name} onChange={(e)=>setEditDraft({...editDraft, name:e.target.value})} />
              <input className="border rounded px-3 py-2 text-sm" placeholder={t('cust.add.phone')} value={editDraft.phone} onChange={(e)=>setEditDraft({...editDraft, phone:e.target.value})} required />
              <input className="border rounded px-3 py-2 text-sm" placeholder={t('cust.add.address')} value={editDraft.address} onChange={(e)=>setEditDraft({...editDraft, address:e.target.value})} />
            </div>
            <div className="p-4 flex gap-2">
              <button className="flex-1 bg-green-600 text-white rounded-xl py-2 text-sm shadow" onClick={saveEdit}>{t('cust.modal.save')}</button>
              <button className="flex-1 bg-white border border-green-200 text-green-700 rounded-xl py-2 text-sm" onClick={()=>setEditing(null)}>{t('cust.modal.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


